import { Fade, Typography } from '@material-ui/core';
import { Info } from '@material-ui/icons';
import {
  ColorDynamic,
  Column,
  Columns,
  Inline,
  Stack,
} from '@superdispatch/ui';
import {
  Alert,
  Box,
  Button,
  DescriptionLineItem,
  TextBox,
} from '@superdispatch/ui-lab';
import { useEffect } from 'react';
import {
  CountryFieldOption,
  countryFieldOptions,
} from 'shared/form/CountryFieldOptions';
import { trackEvent } from 'shared/helpers/Analytics';
import { formatCurrency, formatPlural } from 'shared/helpers/IntlHelpers';
import { useQuery } from 'shared/helpers/RoutingHelpers';
import { useSearchParam } from 'shared/helpers/SearchParamsHelpers';
import { openExternalURL } from 'shared/helpers/URLHelpers';
import { useShowCelebrationNotification } from 'shared/modules/subscription/core/useShowCelebrationNotification';
import { trackSubscriptionEvent } from 'shared/modules/subscription/data/SubscriptionAnalytics';
import {
  useBillingAddress,
  useEstimateTaxPrice,
  useReactivateSubscriptionMutation,
  useSubscription,
  useUpdateSubscriptionSettingsMutation,
} from 'shared/modules/subscription/SubscriptionAPI';
import { LocationState } from 'shared/modules/subscription/SubscriptionSalesTaxDrawer';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { BackButton } from 'shared/ui/BackButton';
import { joinStrings } from 'shared/utils/StringUtils';
import { useShowTrialExpiredDialog } from '../data/useShowTrialExpiredDialog';

interface PaywallPaymentTaxFormProps {
  billingInfo: LocationState;
  onBack: () => void;
}

export function PaywallPaymentTaxForm({
  billingInfo,
  onBack,
}: PaywallPaymentTaxFormProps) {
  const [{ utm_source, utm_medium, utm_content, driver_guid }] = useQuery();
  const { cardDetails, token, vaultToken, seatsCount } = billingInfo;
  const {
    isDriverSeat,
    seatType,
    subscription: subscriptionDetails,
  } = useSubscription();
  const { data: taxPrice } = useEstimateTaxPrice({
    seats_count: seatsCount,
    include_total_invoice: true,
  });
  const { data: billingAddress } = useBillingAddress();
  const [pagePath] = useSearchParam('path');
  const { showCelebrationNotification } = useShowCelebrationNotification();
  const { closeTrialExpiredDialog } = useShowTrialExpiredDialog();
  const { data: settings } = useCarrierSettings();

  const billingState: string = billingAddress?.billing_state || '';
  const billingZip: string = billingAddress?.billing_zip || '';
  const carrierGUID = settings?.carrier.guid;

  const {
    mutate: updateSubscription,
    isPending,
    error,
  } = useUpdateSubscriptionSettingsMutation({
    onSuccess: () => {
      trackEvent('Carrier Upgraded To Pro Plan', {
        utm_source: utm_source || 'paywall',
        utm_medium: utm_medium || pagePath,
        utm_content,
      });
      closeTrialExpiredDialog();
      openExternalURL(pagePath || '/loadboard');
      showCelebrationNotification();
    },
  });

  const {
    mutate: reactivate,
    isPending: isReactivateLoading,
    error: reactivateError,
  } = useReactivateSubscriptionMutation({
    onSuccess: () => {
      trackSubscriptionEvent({
        name: 'Carrier Reactivated Subscription',
        utm_medium,
        carrier_guid: carrierGUID,
        driver_guid,
      });
      closeTrialExpiredDialog();
      showCelebrationNotification();
      openExternalURL('/loadboard');
    },
  });

  useEffect(() => {
    trackSubscriptionEvent({
      name: 'Carrier Opened Subscription Checkout in Paywall',
      carrier_guid: carrierGUID,
    });
  }, [carrierGUID]);

  const country = getCountryName(
    billingAddress?.billing_country as CountryFieldOption,
  );
  const hasCreditCard = !!cardDetails?.creditCard;
  const isSubscriptionCanceled =
    subscriptionDetails?.subscription?.plan?.name === 'lite';
  const hasCustomerCardDetails = subscriptionDetails?.customer?.payment_method;

  const totalTaxPrice: string = taxPrice?.total || '';
  const taxPricePlanAmount: number = taxPrice?.plan?.amount || 0;
  const taxPriceSubtotal: number = taxPrice?.sub_total || 0;
  const unitPrice: number = taxPrice?.plan?.unit_price || 0;

  return (
    <Fade in={!!billingInfo}>
      <Stack space="large">
        <Inline noWrap={true} verticalAlign="center" space="small">
          <BackButton onClick={onBack} />
          <Typography variant="h3">Upgrading Plan</Typography>
        </Inline>
        <Stack space="medium">
          <Box paddingBottom="small">
            <Stack space="large">
              {hasCreditCard && (
                <Stack space="xsmall">
                  <Typography color="textSecondary" variant="h6">
                    CARD DETAILS
                  </Typography>
                  <Typography color="textSecondary">
                    Card Number: •••• {cardDetails.creditCard.details.lastFour}
                  </Typography>
                  <Typography color="textSecondary">
                    Expiration Date:{' '}
                    {cardDetails.creditCard.details.expirationMonth} /{' '}
                    {cardDetails.creditCard.details.expirationYear}
                  </Typography>
                </Stack>
              )}

              <Stack space="xsmall">
                <Typography color="textSecondary" variant="h6">
                  Billing Address
                </Typography>
                <Typography color="textSecondary">
                  {joinStrings(', ', billingState, billingZip, country)}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          <Stack space="small">
            {taxPrice && (
              <Stack space="small">
                <Stack space="xxsmall">
                  <DescriptionLineItem
                    title={
                      <TextBox variant="body" color="primary">
                        Pro plan
                      </TextBox>
                    }
                  >
                    <TextBox align="right" color="primary">
                      {formatCurrency(taxPricePlanAmount)}
                    </TextBox>
                  </DescriptionLineItem>

                  <TextBox color="secondary">
                    {formatCurrency(unitPrice)} / {seatType} / month x{' '}
                    {seatsCount}{' '}
                    {isDriverSeat
                      ? formatPlural(seatsCount, 'driver', 'drivers')
                      : formatPlural(seatsCount, 'seat', 'seats')}
                  </TextBox>
                  <TextBox color="secondary">Billed Monthly</TextBox>
                </Stack>
              </Stack>
            )}

            <Stack space="xsmall">
              <DescriptionLineItem
                title={
                  <TextBox variant="body" color="primary">
                    Subtotal
                  </TextBox>
                }
              >
                <TextBox align="right" color="primary">
                  {formatCurrency(taxPriceSubtotal)}
                </TextBox>
              </DescriptionLineItem>
              {taxPrice?.taxes && taxPrice.taxes.length > 0 && (
                <Columns>
                  <Column>
                    <Box />
                  </Column>
                  <Column width="2/3">
                    <Stack align="right" space="xxsmall">
                      {taxPrice.taxes.map((tax) => {
                        const taxAmount: number = tax.amount || 0;
                        return (
                          <DescriptionLineItem
                            key={tax.name}
                            title={
                              <TextBox color="secondary">
                                {tax.description}
                              </TextBox>
                            }
                          >
                            <TextBox align="right" color="secondary">
                              {formatCurrency(taxAmount)}
                            </TextBox>
                          </DescriptionLineItem>
                        );
                      })}
                    </Stack>
                  </Column>
                </Columns>
              )}
            </Stack>

            <DescriptionLineItem
              title={<TextBox variant="heading-4">Total</TextBox>}
            >
              <TextBox variant="heading-4" align="right">
                {formatCurrency(totalTaxPrice)}
              </TextBox>
            </DescriptionLineItem>
          </Stack>
          <Box paddingTop="xsmall">
            <Button
              pending={isPending || isReactivateLoading}
              onClick={() => {
                if (isSubscriptionCanceled && hasCustomerCardDetails) {
                  reactivate();
                } else {
                  updateSubscription({
                    payment_method_nonce: token,
                    id: token,
                    id_at_vault: vaultToken,
                    seats_count: seatsCount,
                  });
                }
              }}
              size="large"
              fullWidth={true}
            >
              Upgrade Now
            </Button>
          </Box>
        </Stack>
        {(error || reactivateError) && !isPending && !isReactivateLoading && (
          <Box maxWidth="430px" paddingRight="large" paddingLeft="large">
            <Alert
              icon={<Info fontSize="small" htmlColor={ColorDynamic.Red500} />}
              severity="critical"
            >
              {error?.message || reactivateError?.message}
            </Alert>
          </Box>
        )}
      </Stack>
    </Fade>
  );
}

function getCountryName(countryCode: CountryFieldOption) {
  return countryFieldOptions.get(countryCode);
}
